import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Trash2 } from "lucide-react";

interface ProjectCardProps {
  project: {
    id: number;
    uuid?: string;
    title: string;
    bookType: string;
    targetWordCount: number;
    status: string;
    updatedAt: string;
    createdAt: string;
    projectData?: any;
    progress?: {
      overall: number;
      blueprint: number;
      chapters: number;
      metadata: number;
      export: number;
    };
  };
  onDelete: (project: { id: number; uuid?: string }) => void;
  isDeleting?: boolean;
  variant?: 'compact' | 'detailed'; // For different display modes
}

const getBookTypeLabel = (bookType: string) => {
  switch (bookType) {
    case "SHORT_STORY": return "Short Story";
    case "NOVELLA": return "Novella";
    case "NOVEL": return "Novel";
    default: return bookType;
  }
};

const getBookTypeColor = (bookType: string) => {
  switch (bookType) {
    case "SHORT_STORY": return "bg-green-100 text-green-800";
    case "NOVELLA": return "bg-blue-100 text-blue-800";
    case "NOVEL": return "bg-purple-100 text-purple-800";
    default: return "bg-gray-100 text-gray-800";
  }
};

// Fallback progress calculation for projects without progress data
const calculateFallbackProgress = (project: any): number => {
  if (project.status === 'completed') return 100;
  const hasBlueprint = !!project.projectData?.blueprint;
  const chapters = project.projectData?.chapters || [];
  if (!hasBlueprint) return 5;
  if (chapters.length === 0) return 10;
  const completedChapters = chapters.filter((c: any) => c.status === 'finalized').length;
  return 10 + Math.round((completedChapters / chapters.length) * 80);
};

// Calculate chapter progress based on actual chapter data
const calculateChapterProgress = (project: any): number => {
  const chapters = project.projectData?.chapters || [];
  if (chapters.length === 0) return 0;

  // Calculate progress based on chapters with preview content
  const chaptersWithPreview = chapters.filter((c: any) => c.preview && c.preview.trim().length > 0).length;
  const chapterProgress = Math.round((chaptersWithPreview / chapters.length) * 100);

  console.log('📊 Chapter progress calculation:', {
    totalChapters: chapters.length,
    chaptersWithPreview,
    calculatedProgress: chapterProgress,
    sampleChapter: chapters[0] ? {
      id: chapters[0].id,
      title: chapters[0].title,
      hasPreview: !!(chapters[0].preview && chapters[0].preview.trim().length > 0),
      previewLength: chapters[0].preview ? chapters[0].preview.length : 0
    } : null
  });

  return chapterProgress;
};

export default function ProjectCard({
  project,
  onDelete,
  isDeleting = false,
  variant = 'detailed'
}: ProjectCardProps) {
  // Use progress data if available, otherwise fallback to calculation
  const progressData = project.progress || {
    overall: calculateFallbackProgress(project),
    blueprint: project.projectData?.blueprint ? 100 : 0,
    chapters: calculateChapterProgress(project),
    metadata: 0,
    export: 0
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this project? This cannot be undone.')) {
      onDelete(project);
    }
  };

  return (
    <Card className="hover:shadow-xl transition-shadow duration-300 flex flex-col">
      <CardHeader>
        <div className="flex justify-between items-start gap-2">
          <CardTitle className="text-lg leading-tight">{project.title}</CardTitle>
          <div className="flex gap-2">
            {variant === 'detailed' && (
              <Badge>
                {project.uuid ? project.uuid.substring(0, 8) + '...' : `ID: ${project.id}`}
              </Badge>
            )}
            <Badge className={getBookTypeColor(project.bookType)}>
              {getBookTypeLabel(project.bookType)}
            </Badge>
          </div>
        </div>
        <CardDescription>
          Updated {new Date(project.updatedAt).toLocaleDateString()}
        </CardDescription>
      </CardHeader>

      <CardContent className="flex-grow space-y-4">
        {/* Main Progress Bar */}
        <div>
          <div className="flex justify-between text-sm text-muted-foreground mb-1">
            <span>Progress</span>
            <span>{progressData.overall}%</span>
          </div>
          <Progress value={progressData.overall} className="h-2" />
        </div>

        {/* Detailed Progress Breakdown - only shown in detailed variant */}
        {variant === 'detailed' && (
          <>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-600">Blueprint:</span>
                <span>{progressData.blueprint}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Chapters:</span>
                <span>{progressData.chapters}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Metadata:</span>
                <span>{progressData.metadata}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Export:</span>
                <span>{progressData.export}%</span>
              </div>
            </div>

            {/* Chapters Progress */}
            <div className="mt-2">
              <h4 className="text-sm font-medium mb-1">
                Chapters Progress: {progressData.chapters}%
              </h4>
              <Progress
                value={progressData.chapters}
                className="h-1"
              />
            </div>
          </>
        )}
      </CardContent>

      {/* Actions */}
      <div className="p-4 pt-0 flex flex-col gap-2 sm:flex-row sm:items-center">
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive w-10 h-10 sm:mr-2"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          <Trash2 className="h-4 w-4"/>
        </Button>
        <Link to={`/story-workspace/${project.uuid || project.id}`} className="w-full flex-grow">
          <Button className="w-full bg-primary hover:bg-primary/90 h-12 sm:h-auto">
            Continue Writing
          </Button>
        </Link>
      </div>
    </Card>
  );
}